import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Shield, Save, Plus, Trash2, FileCheck, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function LegalConfigAdmin() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setS({
      ...data,
      rgpd_data_types: data.rgpd_data_types || [],
      rgpd_data_purposes: data.rgpd_data_purposes || [],
    });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Configuration RGPD enregistrée");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const addListItem = (key, value = "") => {
    setS({ ...s, [key]: [...(s[key] || []), value] });
  };
  const updateListItem = (key, i, v) => {
    const arr = [...(s[key] || [])];
    arr[i] = v;
    setS({ ...s, [key]: arr });
  };
  const removeListItem = (key, i) => {
    setS({ ...s, [key]: (s[key] || []).filter((_, idx) => idx !== i) });
  };

  if (!s) return <div className="p-10 text-sm text-[hsl(var(--muted-foreground))]">Chargement…</div>;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="legal-config-admin">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[color:var(--brand-sky)] mb-1">
            <Shield className="w-3.5 h-3.5" />Conformité
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Générateur RGPD</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Les mentions légales et la politique de confidentialité sont générées dynamiquement à partir de ces champs. Une fois remplis, vos pages <code>/mentions-legales</code> et <code>/confidentialite</code> sont à jour.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)]" data-testid="save-rgpd">
          <Save className="w-4 h-4 mr-1.5" />{saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Link to="/mentions-legales" target="_blank" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border bg-white hover:bg-[color:var(--brand-sky)]/5">
          <ExternalLink className="w-3 h-3" />Prévisualiser /mentions-legales
        </Link>
        <Link to="/confidentialite" target="_blank" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border bg-white hover:bg-[color:var(--brand-sky)]/5">
          <ExternalLink className="w-3 h-3" />Prévisualiser /confidentialite
        </Link>
      </div>

      {/* Responsable de publication */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><FileCheck className="w-4 h-4" />Responsable de publication</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Nom complet *</Label>
            <Input value={s.rgpd_publication_manager || ""} onChange={e => setS({ ...s, rgpd_publication_manager: e.target.value })} placeholder="Ex: Jean Dupont" data-testid="rgpd-manager-name" />
          </div>
          <div>
            <Label>Fonction</Label>
            <Input value={s.rgpd_publication_manager_role || ""} onChange={e => setS({ ...s, rgpd_publication_manager_role: e.target.value })} placeholder="Ex: Gérant, Président" />
          </div>
        </div>
      </Card>

      {/* DPO */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Shield className="w-4 h-4" />Délégué à la protection des données (DPO) <span className="text-xs text-[hsl(var(--muted-foreground))] font-normal">— optionnel</span></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Nom du DPO</Label>
            <Input value={s.rgpd_dpo_name || ""} onChange={e => setS({ ...s, rgpd_dpo_name: e.target.value })} placeholder="Laisser vide si pas de DPO" />
          </div>
          <div>
            <Label>Email du DPO</Label>
            <Input type="email" value={s.rgpd_dpo_email || ""} onChange={e => setS({ ...s, rgpd_dpo_email: e.target.value })} placeholder="dpo@entreprise.fr" />
          </div>
        </div>
      </Card>

      {/* Hébergeur */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">Hébergeur du site</div>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>Nom / société</Label>
            <Input value={s.rgpd_host_name || ""} onChange={e => setS({ ...s, rgpd_host_name: e.target.value })} data-testid="rgpd-host-name" />
          </div>
          <div>
            <Label>Adresse</Label>
            <Input value={s.rgpd_host_address || ""} onChange={e => setS({ ...s, rgpd_host_address: e.target.value })} />
          </div>
          <div>
            <Label>Contact (email/tel)</Label>
            <Input value={s.rgpd_host_contact || ""} onChange={e => setS({ ...s, rgpd_host_contact: e.target.value })} />
          </div>
        </div>
      </Card>

      {/* Données collectées */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">Données personnelles collectées</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">Listez précisément les types de données que votre site collecte (une ligne = un type).</div>
        <div className="space-y-2">
          {(s.rgpd_data_types || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={t} onChange={e => updateListItem("rgpd_data_types", i, e.target.value)} className="flex-1" data-testid={`rgpd-data-type-${i}`} />
              <Button variant="ghost" size="sm" onClick={() => removeListItem("rgpd_data_types", i)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => addListItem("rgpd_data_types", "")}>
          <Plus className="w-3 h-3 mr-1" />Ajouter un type de donnée
        </Button>
      </Card>

      {/* Finalités */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">Finalités d'utilisation</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">Expliquez pourquoi vous collectez ces données. RGPD exige la transparence des finalités.</div>
        <div className="space-y-2">
          {(s.rgpd_data_purposes || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={t} onChange={e => updateListItem("rgpd_data_purposes", i, e.target.value)} className="flex-1" data-testid={`rgpd-purpose-${i}`} />
              <Button variant="ghost" size="sm" onClick={() => removeListItem("rgpd_data_purposes", i)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => addListItem("rgpd_data_purposes", "")}>
          <Plus className="w-3 h-3 mr-1" />Ajouter une finalité
        </Button>
      </Card>

      {/* Conservation + non-revente */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">Conservation & éthique</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Durée de conservation (années)</Label>
            <Input type="number" min={1} max={20} value={s.rgpd_data_retention_years ?? 3} onChange={e => setS({ ...s, rgpd_data_retention_years: +e.target.value })} data-testid="rgpd-retention" />
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Au-delà, les données doivent être supprimées (hors obligations légales).</div>
          </div>
          <div className="flex items-start gap-3 pt-6">
            <Switch checked={!!s.rgpd_no_resale_mention} onCheckedChange={v => setS({ ...s, rgpd_no_resale_mention: v })} id="noresale" data-testid="rgpd-no-resale" />
            <div>
              <Label htmlFor="noresale" className="text-sm">Afficher « Données non revendues »</Label>
              <p className="text-[10px] text-[hsl(var(--muted-foreground))]">Ajoute une mention verte rassurante en haut de la page confidentialité.</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Cookies */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">Cookies</div>
        <Label>Texte d'information cookies (optionnel — sinon texte par défaut)</Label>
        <Textarea rows={3} value={s.rgpd_cookies_text || ""} onChange={e => setS({ ...s, rgpd_cookies_text: e.target.value })} placeholder="Ex: Ce site utilise Google Analytics et des cookies techniques…" />
      </Card>

      {/* Extra */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">Dispositions particulières</div>
        <Label>Paragraphes supplémentaires (ajoutés aux deux pages si renseignés)</Label>
        <Textarea rows={4} value={s.rgpd_legal_extra || ""} onChange={e => setS({ ...s, rgpd_legal_extra: e.target.value })} placeholder="Mentions spécifiques à votre secteur (carte professionnelle, garantie décennale, etc.)" />
      </Card>

      <div className="rounded-lg bg-[color:var(--brand-sky)]/5 border border-[color:var(--brand-sky)]/30 p-4 text-sm text-[color:var(--brand-navy)] flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-[color:var(--brand-sky)] shrink-0 mt-0.5" />
        <div>
          <strong>Astuce :</strong> les informations de raison sociale, SIRET, adresse et contact sont reprises depuis <Link to="/admin/settings" className="underline text-[color:var(--brand-sky)]">Paramètres généraux</Link>. Pensez à les compléter.
        </div>
      </div>
    </div>
  );
}
